/**
 * SustainabilityIntelligencePanel - Phase 5 Part A
 * 
 * Displays Control Intelligence evaluation with:
 * - Sustainability Score (0-100)
 * - Risk Level
 * - Drift Status for each KPI
 * - Reaction Plan Adequacy
 * - Ownership Clarity
 * - Required Improvements
 * 
 * Tone: Audit-level clarity. Not alarmist. Not casual.
 */
import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import {
  Shield,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Activity,
  Users,
  Clock,
  Target,
  ChevronDown,
  ChevronUp,
  Loader2,
  Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge configurations
const RISK_LEVELS = {
  Low: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  Moderate: { color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: AlertTriangle },
  High: { color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle },
};

const DRIFT_STATUS = {
  Stable: { color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle2, label: "Stable" },
  "Early Warning": { color: "bg-amber-500/20 text-amber-400", icon: TrendingUp, label: "Early Warning" },
  "Out of Control": { color: "bg-red-500/20 text-red-400", icon: AlertTriangle, label: "Out of Control" },
  "Insufficient Data": { color: "bg-slate-500/20 text-slate-400", icon: Info, label: "Insufficient Data" },
};

const ADEQUACY_LEVELS = {
  Strong: { color: "text-emerald-400", bg: "bg-emerald-500/10" },
  Adequate: { color: "text-amber-400", bg: "bg-amber-500/10" },
  Weak: { color: "text-red-400", bg: "bg-red-500/10" },
  Defined: { color: "text-emerald-400", bg: "bg-emerald-500/10" },
  Partial: { color: "text-amber-400", bg: "bg-amber-500/10" },
  Ambiguous: { color: "text-red-400", bg: "bg-red-500/10" },
  Linked: { color: "text-emerald-400", bg: "bg-emerald-500/10" },
  "Not Linked": { color: "text-red-400", bg: "bg-red-500/10" },
};

export default function SustainabilityIntelligencePanel({ projectId, token }) {
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [expanded, setExpanded] = useState({
    kpiAnalyses: false,
    improvements: true,
    guardrails: false,
  });

  // Load persisted evaluation on mount
  const loadPersistedEvaluation = useCallback(async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/control`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.sustainability_evaluation) {
          setEvaluation(data.sustainability_evaluation);
        }
      }
    } catch (error) {
      console.error("Failed to load sustainability evaluation:", error);
    } finally {
      setInitialLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    loadPersistedEvaluation();
  }, [loadPersistedEvaluation]);

  const runEvaluation = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/control/evaluate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setEvaluation(data);
      } else {
        const err = await response.json();
        console.error("Evaluation failed:", err);
      }
    } catch (error) {
      console.error("Failed to run evaluation:", error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-400";
    if (score >= 60) return "text-amber-400";
    return "text-red-400";
  };

  const getScoreRing = (score) => {
    if (score >= 80) return "ring-emerald-500/30";
    if (score >= 60) return "ring-amber-500/30";
    return "ring-red-500/30";
  };

  if (initialLoading) {
    return (
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg p-6 text-center py-8">
        <Loader2 className="w-8 h-8 text-muted-foreground mx-auto mb-3 animate-spin" />
        <p className="text-muted-foreground">Loading sustainability data...</p>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/10 rounded-lg">
              <Shield className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-semibold text-[hsl(var(--foreground))]">
                Sustainability Intelligence
              </h3>
              <p className="text-sm text-muted-foreground">
                Phase 5 Control Monitoring
              </p>
            </div>
          </div>
          <Button
            onClick={runEvaluation}
            disabled={loading}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="run-sustainability-eval-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Evaluating...
              </>
            ) : (
              <>
                <Activity className="w-4 h-4 mr-2" />
                Run Sustainability Analysis
              </>
            )}
          </Button>
        </div>

        <div className="text-center py-8 border border-dashed border-[hsl(var(--border))] rounded-lg">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground">
            Run sustainability analysis to evaluate drift detection, reaction plans, and ownership
          </p>
        </div>
      </div>
    );
  }

  const riskConfig = RISK_LEVELS[evaluation.risk_level] || RISK_LEVELS.Moderate;
  const driftConfig = DRIFT_STATUS[evaluation.drift_status] || DRIFT_STATUS.Stable;
  const RiskIcon = riskConfig.icon;
  const DriftIcon = driftConfig.icon;

  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg p-6 space-y-6" data-testid="sustainability-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-cyan-500/10 rounded-lg">
            <Shield className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-semibold text-[hsl(var(--foreground))]">
              Sustainability Intelligence
            </h3>
            <p className="text-sm text-muted-foreground">
              Last evaluated: {new Date(evaluation.evaluated_at).toLocaleString()}
            </p>
          </div>
        </div>
        <Button
          onClick={runEvaluation}
          disabled={loading}
          variant="outline"
          size="sm"
          data-testid="refresh-sustainability-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {/* Main Score and Status Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Sustainability Score */}
        <div className={`p-4 bg-[hsl(var(--background))] rounded-lg border border-[hsl(var(--border))] ring-2 ${getScoreRing(evaluation.sustainability_score)}`}>
          <div className="text-sm text-muted-foreground mb-1">Sustainability Score</div>
          <div className={`text-3xl font-bold ${getScoreColor(evaluation.sustainability_score)}`} data-testid="sustainability-score">
            {evaluation.sustainability_score}
          </div>
          <div className={`text-sm ${getScoreColor(evaluation.sustainability_score)}`}>
            / 100
          </div>
        </div>

        {/* Risk Level */}
        <div className="p-4 bg-[hsl(var(--background))] rounded-lg border border-[hsl(var(--border))]">
          <div className="text-sm text-muted-foreground mb-1">Risk Level</div>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${riskConfig.color}`} data-testid="risk-level">
            <RiskIcon className="w-4 h-4" />
            <span className="font-medium">{evaluation.risk_level}</span>
          </div>
        </div>

        {/* Drift Status */}
        <div className="p-4 bg-[hsl(var(--background))] rounded-lg border border-[hsl(var(--border))]">
          <div className="text-sm text-muted-foreground mb-1">Drift Status</div>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${driftConfig.color}`} data-testid="drift-status">
            <DriftIcon className="w-4 h-4" />
            <span className="font-medium">{driftConfig.label}</span>
          </div>
        </div>

        {/* KPI-Driver Linkage */}
        <div className="p-4 bg-[hsl(var(--background))] rounded-lg border border-[hsl(var(--border))]">
          <div className="text-sm text-muted-foreground mb-1">Driver Linkage</div>
          <div className={`font-medium ${ADEQUACY_LEVELS[evaluation.kpi_driver_linkage]?.color || "text-muted-foreground"}`} data-testid="driver-linkage">
            {evaluation.kpi_driver_linkage}
          </div>
          {evaluation.validated_drivers_from_analyze?.length > 0 && (
            <div className="text-xs text-muted-foreground mt-1">
              Drivers: {evaluation.validated_drivers_from_analyze.slice(0, 2).join(", ")}
            </div>
          )}
        </div>
      </div>

      {/* Adequacy Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Reaction Plan */}
        <div className={`p-4 rounded-lg ${ADEQUACY_LEVELS[evaluation.reaction_plan_adequacy]?.bg || "bg-slate-500/10"}`}>
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Reaction Plans</span>
          </div>
          <div className={`font-semibold ${ADEQUACY_LEVELS[evaluation.reaction_plan_adequacy]?.color || "text-muted-foreground"}`} data-testid="reaction-plan-adequacy">
            {evaluation.reaction_plan_adequacy}
          </div>
        </div>

        {/* Ownership */}
        <div className={`p-4 rounded-lg ${ADEQUACY_LEVELS[evaluation.ownership_clarity]?.bg || "bg-slate-500/10"}`}>
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Ownership</span>
          </div>
          <div className={`font-semibold ${ADEQUACY_LEVELS[evaluation.ownership_clarity]?.color || "text-muted-foreground"}`} data-testid="ownership-clarity">
            {evaluation.ownership_clarity}
          </div>
        </div>

        {/* Monitoring */}
        <div className={`p-4 rounded-lg ${ADEQUACY_LEVELS[evaluation.monitoring_adequacy]?.bg || "bg-slate-500/10"}`}>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Monitoring</span>
          </div>
          <div className={`font-semibold ${ADEQUACY_LEVELS[evaluation.monitoring_adequacy]?.color || "text-muted-foreground"}`} data-testid="monitoring-adequacy">
            {evaluation.monitoring_adequacy}
          </div>
        </div>
      </div>

      {/* Guardrails Applied */}
      {evaluation.guardrails_applied?.length > 0 && (
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4">
          <button
            onClick={() => setExpanded({ ...expanded, guardrails: !expanded.guardrails })}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="font-medium text-amber-400">
                {evaluation.guardrails_applied.length} Guardrail(s) Active
              </span>
            </div>
            {expanded.guardrails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expanded.guardrails && (
            <div className="mt-3 space-y-2">
              {evaluation.guardrails_applied.map((g, i) => (
                <div key={i} className="text-sm text-amber-300/80 pl-6">
                  {g}
                </div>
              ))}
              {evaluation.score_caps?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-500/20">
                  <div className="text-xs text-amber-400/70 mb-2">Score Caps Applied:</div>
                  {evaluation.score_caps.map((cap, i) => (
                    <div key={i} className="text-sm text-amber-300/80 pl-6">
                      {cap.guardrail}: capped to {cap.cap} (was {cap.original})
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Required Improvements */}
      {evaluation.required_improvements?.length > 0 && (
        <div className="border border-[hsl(var(--border))] rounded-lg">
          <button
            onClick={() => setExpanded({ ...expanded, improvements: !expanded.improvements })}
            className="flex items-center justify-between w-full p-4 text-left"
          >
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-cyan-400" />
              <span className="font-medium text-[hsl(var(--foreground))]">
                Required Improvements ({evaluation.required_improvements.length})
              </span>
            </div>
            {expanded.improvements ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expanded.improvements && (
            <div className="px-4 pb-4 space-y-3">
              {evaluation.required_improvements.map((imp, i) => (
                <div key={i} className="bg-[hsl(var(--background))] rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">
                      {imp.criterion}
                    </span>
                  </div>
                  <div className="text-sm text-[hsl(var(--foreground))] mb-1">
                    {imp.issue}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {imp.action}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* KPI Drift Analyses */}
      {evaluation.kpi_analyses?.length > 0 && (
        <div className="border border-[hsl(var(--border))] rounded-lg">
          <button
            onClick={() => setExpanded({ ...expanded, kpiAnalyses: !expanded.kpiAnalyses })}
            className="flex items-center justify-between w-full p-4 text-left"
          >
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              <span className="font-medium text-[hsl(var(--foreground))]">
                KPI Drift Analysis ({evaluation.kpi_analyses.length} KPIs)
              </span>
            </div>
            {expanded.kpiAnalyses ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expanded.kpiAnalyses && (
            <div className="px-4 pb-4 space-y-3">
              {evaluation.kpi_analyses.map((kpi, i) => {
                const kpiDrift = DRIFT_STATUS[kpi.status === "stable" ? "Stable" : 
                                              kpi.status === "early_warning" ? "Early Warning" :
                                              kpi.status === "out_of_control" ? "Out of Control" : "Insufficient Data"];
                const KpiIcon = kpiDrift?.icon || Info;
                
                return (
                  <div key={i} className="bg-[hsl(var(--background))] rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-[hsl(var(--foreground))]">
                        {kpi.kpi_name}
                      </span>
                      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${kpiDrift?.color || "bg-slate-500/20 text-slate-400"}`}>
                        <KpiIcon className="w-3 h-3" />
                        {kpi.status.replace(/_/g, " ")}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-2">
                      <div className="text-muted-foreground">
                        Data Points: <span className="text-[hsl(var(--foreground))]">{kpi.data_points}</span>
                      </div>
                      <div className="text-muted-foreground">
                        Current: <span className="text-[hsl(var(--foreground))]">{kpi.current_value ?? "N/A"}</span>
                      </div>
                      <div className="text-muted-foreground">
                        Target: <span className="text-[hsl(var(--foreground))]">{kpi.target ?? "N/A"}</span>
                      </div>
                      <div className="text-muted-foreground">
                        UCL/LCL: <span className="text-[hsl(var(--foreground))]">
                          {kpi.control_limits?.ucl ?? "—"} / {kpi.control_limits?.lcl ?? "—"}
                        </span>
                      </div>
                    </div>

                    {/* Detection Flags */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {kpi.trend_detected && (
                        <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> Trend
                        </span>
                      )}
                      {kpi.mean_shift_detected && (
                        <span className="text-xs bg-red-500/10 text-red-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <Minus className="w-3 h-3" /> Mean Shift
                        </span>
                      )}
                      {kpi.variance_change_detected && (
                        <span className="text-xs bg-red-500/10 text-red-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <Activity className="w-3 h-3" /> Variance Change
                        </span>
                      )}
                      {kpi.threshold_breach && (
                        <span className="text-xs bg-red-500/10 text-red-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Threshold Breach
                        </span>
                      )}
                    </div>

                    {/* Findings */}
                    {kpi.findings?.length > 0 && (
                      <div className="space-y-1">
                        {kpi.findings.map((f, fi) => (
                          <div key={fi} className="text-xs text-muted-foreground pl-2 border-l-2 border-slate-600">
                            {f}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
